// Rejection-code → human copy for the template upload flow. One entry per
// value of supplierSourceUploadRejectionCodeV1Schema (supplierUploadContracts.js) —
// kept as an exhaustive map (not a switch) so a new backend code without an
// entry here is obvious in review rather than silently falling through.
//
// i18n: builder (not a static map) so every title/body routes through i18n —
// callers inside a component pass their bound `t` (useTranslation('freeSupplier'))
// for a live-reactive value; the i18n singleton default keeps every
// non-React caller (this file's own test suite included) working unchanged,
// same pattern as helpers/validationMessages.js's resolveValidationMessage.
import i18n from 'i18n';

const buildRejectionCopy = (t) => ({
  FILE_TOO_LARGE: {
    title: t('freeSupplier:upload.rejection.fileTooLarge.title', "That file's too large"),
    body: t(
      'freeSupplier:upload.rejection.fileTooLarge.body',
      'The workbook exceeds the 14 MB limit. Export a smaller copy and try again.',
    ),
  },
  UNSUPPORTED_MEDIA_TYPE: {
    title: t('freeSupplier:upload.rejection.unsupportedMediaType.title', 'Unsupported file'),
    body: t(
      'freeSupplier:upload.rejection.unsupportedMediaType.body',
      'Only a single .xlsx workbook is accepted.',
    ),
  },
  SIGNATURE_MISMATCH: {
    title: t(
      'freeSupplier:upload.rejection.signatureMismatch.title',
      "That file isn't a valid workbook",
    ),
    body: t(
      'freeSupplier:upload.rejection.signatureMismatch.body',
      "The file's contents don't match an Excel workbook. Re-export it and try again.",
    ),
  },
  INVALID_ARCHIVE: {
    title: t('freeSupplier:upload.rejection.invalidArchive.title', 'The workbook is corrupted'),
    body: t(
      'freeSupplier:upload.rejection.invalidArchive.body',
      "We couldn't open this file as a workbook. Re-export it from your source and try again.",
    ),
  },
  ARCHIVE_LIMIT_EXCEEDED: {
    title: t(
      'freeSupplier:upload.rejection.archiveLimitExceeded.title',
      'The workbook is too complex',
    ),
    body: t(
      'freeSupplier:upload.rejection.archiveLimitExceeded.body',
      'This file contains more sheets or embedded content than we can safely process.',
    ),
  },
  MACRO_ENABLED: {
    title: t(
      'freeSupplier:upload.rejection.macroEnabled.title',
      'Macro-enabled workbooks are not accepted',
    ),
    body: t(
      'freeSupplier:upload.rejection.macroEnabled.body',
      'Save a copy as a standard .xlsx (without macros) and upload that instead.',
    ),
  },
  EXTERNAL_LINK: {
    title: t('freeSupplier:upload.rejection.externalLink.title', 'External links are not accepted'),
    body: t(
      'freeSupplier:upload.rejection.externalLink.body',
      'Remove any links to external workbooks or data sources, then re-upload.',
    ),
  },
  DANGEROUS_FORMULA: {
    title: t('freeSupplier:upload.rejection.dangerousFormula.title', 'Unsafe formula detected'),
    body: t(
      'freeSupplier:upload.rejection.dangerousFormula.body',
      'This workbook contains a formula we cannot safely evaluate. Replace it with a static value.',
    ),
  },
  WORKBOOK_LIMIT_EXCEEDED: {
    title: t(
      'freeSupplier:upload.rejection.workbookLimitExceeded.title',
      'The workbook is too large to process',
    ),
    body: t(
      'freeSupplier:upload.rejection.workbookLimitExceeded.body',
      'Reduce the number of rows/sheets and try again.',
    ),
  },
  PARSER_TIMEOUT: {
    title: t(
      'freeSupplier:upload.rejection.parserTimeout.title',
      'This template took too long to process',
    ),
    body: t(
      'freeSupplier:upload.rejection.parserTimeout.body',
      'Try again — repeated timeouts usually mean an unusually heavy file.',
    ),
  },
  PARSER_BUSY: {
    title: t('freeSupplier:upload.rejection.parserBusy.title', 'Processing at capacity'),
    body: t(
      'freeSupplier:upload.rejection.parserBusy.body',
      "We're at capacity right now — please retry in a moment.",
    ),
  },
  UNSUPPORTED_TEMPLATE: {
    title: t(
      'freeSupplier:upload.rejection.unsupportedTemplate.title',
      "We don't recognize this template",
    ),
    body: t(
      'freeSupplier:upload.rejection.unsupportedTemplate.body',
      'Only the EU Communication and EFDA templates are supported.',
    ),
  },
  PARSE_FAILED: {
    title: t('freeSupplier:upload.rejection.parseFailed.title', "We couldn't read this template"),
    body: t(
      'freeSupplier:upload.rejection.parseFailed.body',
      'The workbook could not be parsed. Check the template matches the expected layout.',
    ),
  },
  PARTIAL_EXTRACTION: {
    title: t('freeSupplier:upload.rejection.partialExtraction.title', 'Incomplete template data'),
    body: t(
      'freeSupplier:upload.rejection.partialExtraction.body',
      "This template didn't produce a complete set of emissions and diagram data.",
    ),
  },
  INVALID_INSTALLATION: {
    title: t(
      'freeSupplier:upload.rejection.invalidInstallation.title',
      'Installation not available',
    ),
    body: t(
      'freeSupplier:upload.rejection.invalidInstallation.body',
      'The selected installation is not available in your workspace. Choose another and try again.',
    ),
  },
  REPORTING_PERIOD_REQUIRED: {
    title: t(
      'freeSupplier:upload.rejection.reportingPeriodRequired.title',
      'Reporting period needed',
    ),
    body: t(
      'freeSupplier:upload.rejection.reportingPeriodRequired.body',
      'This template does not state a reporting period. Enter it below and the file will be sent again.',
    ),
  },
  TEMPLATE_DATA_INVALID: {
    title: t(
      'freeSupplier:upload.rejection.templateDataInvalid.title',
      'Some template data could not be read',
    ),
    body: t(
      'freeSupplier:upload.rejection.templateDataInvalid.body',
      'Correct the cells listed below in the workbook and upload it again.',
    ),
  },
});

const buildFallbackCopy = (t) => ({
  title: t('freeSupplier:upload.rejection.fallback.title', "We couldn't process that file"),
  body: '',
});

// code + the server's own message → { title, body }. The server message is
// preferred for `body` when present (it may carry more specific detail than
// the static copy), falling back to the static copy otherwise. `t` defaults
// to the i18n singleton for non-React callers (e.g. this file's test suite).
export function supplierUploadRejectionCopy(code, serverMessage, t = i18n.t.bind(i18n)) {
  const copy = buildRejectionCopy(t)[code] || buildFallbackCopy(t);
  return { title: copy.title, body: serverMessage || copy.body };
}

// The one rejection the supplier can resolve without touching the workbook:
// entering the period re-sends the same file with it.
export const isSupplierUploadPeriodRequiredCode = (code) => code === 'REPORTING_PERIOD_REQUIRED';

// A rejection only a corrected workbook can clear: sending the same file again
// is rejected again, so no retry of it is offered.
export const isSupplierUploadWorkbookFixCode = (code) => code === 'TEMPLATE_DATA_INVALID';

// A period issue to show when the rejection carried none of its own, so the
// prompt still has something to ask for.
export const SUPPLIER_UPLOAD_PERIOD_ISSUE_FALLBACK = Object.freeze({
  code: 'TEMPLATE_REPORTING_PERIOD_MISSING',
  path: [],
  message: 'The reporting period is missing',
  severity: 'error',
  scope: 'file',
  resolution: 'reportingPeriod',
});

// PARSER_BUSY is the one code that also arrives as a 429 outside the ingestion
// pipeline (a slot could not be acquired at all) — same code, same copy either way.
export const isSupplierUploadBusyCode = (code) => code === 'PARSER_BUSY';

// INVALID_INSTALLATION (S10 backend amendment) now also covers the
// "detect from template" path finding no installation to attach to — the
// dropzone surfaces a dedicated per-file nudge for this code (pick one
// explicitly via the picker, then retry) instead of the generic rejection
// title, since the fix is always "choose an installation", never "retry as-is".
export const isSupplierUploadMissingInstallationCode = (code) => code === 'INVALID_INSTALLATION';

export const SUPPLIER_UPLOAD_MISSING_INSTALLATION_COPY =
  'This file needs an installation — choose one above, then retry.';
